import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import Card from '@/components/Card';
import styles from './SettingsPage.module.css';

interface EditableSettingProps {
  label: string;
  value: string;
  type?: string;
  onSave: (value: string) => void;
}

function EditableSetting({ label, value, type = 'text', onSave }: EditableSettingProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const handleClick = () => {
    setDraft(value);
    setEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSave(draft);
      setEditing(false);
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  const handleBlur = () => {
    onSave(draft);
    setEditing(false);
  };

  return (
    <div className={styles.settingRow}>
      <span className={styles.settingLabel}>{label}</span>
      {editing ? (
        <input
          className={styles.settingInput}
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoFocus
        />
      ) : (
        <span className={styles.settingValue} onClick={handleClick}>
          {value || 'Click to set'}
        </span>
      )}
    </div>
  );
}

const DONATION_LINK = import.meta.env.VITE_STRIPE_DONATION_LINK as string | undefined;

function SupportSection() {
  const hasLink = !!DONATION_LINK;

  return (
    <div className={styles.supportCard}>
      <div className={styles.supportGlow} />
      <div className={styles.supportIconWrap}>
        <span role="img" aria-label="coffee">☕</span>
      </div>
      <p className={styles.supportTitle}>Buy beryl a Coffee</p>
      <p className={styles.supportDesc}>
        beryl on the web app is free, however she wasn't free to build. A small donation helps to keep her improving and eventually get her on the app store.
      </p>
      <div className={styles.amounts}>
        <span className={styles.amountChip}>$3</span>
        <span className={styles.amountChip}>$5</span>
        <span className={styles.amountChip}>$10</span>
      </div>
      {hasLink ? (
        <a
          href={DONATION_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.donateBtn}
        >
          <span>Donate via Stripe</span>
          <span>→</span>
        </a>
      ) : (
        <button className={`${styles.donateBtn} ${styles.donateBtnDisabled}`} disabled>
          Coming soon
        </button>
      )}
      <p className={styles.stripeNote}>
        <span>🔒</span>
        <span>Secure payment via Stripe. No account needed.</span>
      </p>
    </div>
  );
}

export default function SettingsPage() {
  const { settings, setSetting } = useAppData();
  const { user, signOut } = useAuth();

  const email = user?.email || '';
  const firstLetter = email.charAt(0).toUpperCase();
  const userName = settings.userName || '';
  const dailyProteinGoal = settings.dailyProteinGoal || '120';
  const dailyCalorieGoal = settings.dailyCalorieGoal || '2000';
  const recoveryStartDate = settings.recoveryStartDate || '';

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Settings</h1>

      <Card>
        <div className={styles.profileSection}>
          <div className={styles.avatar}>{firstLetter || '?'}</div>
          <div className={styles.profileInfo}>
            <span className={styles.profileName}>{userName || 'User'}</span>
            <span className={styles.profileEmail}>{email}</span>
          </div>
        </div>
      </Card>

      <h2 className={styles.sectionTitle}>Preferences</h2>
      <Card>
        <EditableSetting
          label="Name"
          value={userName}
          onSave={(v) => setSetting('userName', v)}
        />
        <EditableSetting
          label="Daily Protein Goal (g)"
          value={dailyProteinGoal}
          type="number"
          onSave={(v) => setSetting('dailyProteinGoal', v)}
        />
        <EditableSetting
          label="Daily Calorie Goal"
          value={dailyCalorieGoal}
          type="number"
          onSave={(v) => setSetting('dailyCalorieGoal', v)}
        />
        <EditableSetting
          label="Recovery Start Date"
          value={recoveryStartDate}
          type="date"
          onSave={(v) => setSetting('recoveryStartDate', v)}
        />
      </Card>

      <h2 className={styles.sectionTitle}>Support beryl</h2>
      <SupportSection />

      <h2 className={styles.sectionTitle}>Help</h2>
      <Card>
        <div className={styles.legalLinks}>
          <Link to="/support" className={styles.legalLink}>
            <span>Support & FAQ</span>
            <span className={styles.legalArrow}>→</span>
          </Link>
        </div>
      </Card>

      <h2 className={styles.sectionTitle}>Legal</h2>
      <Card>
        <div className={styles.legalLinks}>
          <Link to="/disclaimer" className={styles.legalLink}>
            <span>Medical Disclaimer</span>
            <span className={styles.legalArrow}>→</span>
          </Link>
          <div className={styles.legalDivider} />
          <Link to="/privacy" className={styles.legalLink}>
            <span>Privacy Policy</span>
            <span className={styles.legalArrow}>→</span>
          </Link>
          <div className={styles.legalDivider} />
          <Link to="/terms" className={styles.legalLink}>
            <span>Terms of Service</span>
            <span className={styles.legalArrow}>→</span>
          </Link>
        </div>
      </Card>

      <h2 className={styles.sectionTitle}>Account</h2>
      <Card>
        <button className={styles.signOutBtn} onClick={signOut}>
          Sign Out
        </button>
      </Card>
    </div>
  );
}
